import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { accountingApi } from '../../../config/apiClient';
import { ACCOUNTING_API } from '../../../config/api';
import { useAuth } from '../../../contexts/AuthContext';
import { InventoryService } from '../inventory/services/inventoryService';
import LotPicker from '../inventory/lots/LotPicker';
import { emptyLotSelection, type LotSelection } from '../inventory/types/lots.types';

export interface AdjustTarget {
  productId: string;
  productName: string;
  unit?: string;
  purchasePrice?: number;
  /** Seçili depodaki toplam miktar */
  currentQuantity: number;
  /** Depodaki tek stok satırı (birden çok lokasyon satırı varsa undefined) */
  warehouseStockId?: string;
  trackLots?: boolean;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  companyId: string | null;
  warehouseId: string;
  warehouseName: string;
  target: AdjustTarget | null;
}

type Mode = 'count' | 'delta';

interface MovementRow {
  id: string;
  date: string;
  direction: string;
  quantity: number;
  previousQuantity?: number;
  newQuantity?: number;
  movementType?: string;
  description?: string;
  createdBy?: string;
}

const HISTORY_SIZE = 10;

const mapMovement = (m: any): MovementRow => ({
  id: m.id ?? m.Id,
  date: m.date ?? m.Date ?? m.createdAt ?? m.CreatedAt,
  direction: m.direction ?? m.Direction ?? '',
  quantity: Number(m.quantity ?? m.Quantity ?? 0),
  previousQuantity: m.previousQuantity ?? m.PreviousQuantity,
  newQuantity: m.newQuantity ?? m.NewQuantity,
  movementType: m.movementType ?? m.MovementType,
  description: m.description ?? m.Description,
  createdBy: m.createdBy ?? m.CreatedBy,
});

const WarehouseStockAdjustDialog: React.FC<Props> = ({
  open, onClose, onSaved, companyId, warehouseId, warehouseName, target,
}) => {
  const { currentUser } = useAuth();
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const localeCode = isTr ? 'tr-TR' : 'en-US';
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);

  const [mode, setMode] = useState<Mode>('count');
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [lotSelection, setLotSelection] = useState<LotSelection>(emptyLotSelection());
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<MovementRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !target) return;
    setMode('count');
    setValue(String(target.currentQuantity));
    setReason('');
    setError(null);
    setLotSelection(emptyLotSelection());

    let cancelled = false;
    setHistoryLoading(true);
    setHistoryError(null);
    accountingApi
      .get(ACCOUNTING_API.STOCK_MOVEMENTS_PAGED({
        page: 1,
        pageSize: HISTORY_SIZE,
        warehouseId,
        productId: target.productId,
      }))
      .then((res) => {
        if (cancelled) return;
        const rows: any[] = res.data?.data ?? res.data?.Data ?? [];
        setHistory(rows.map(mapMovement));
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[WarehouseStockAdjustDialog] Hareket geçmişi yüklenemedi:', err);
        setHistory([]);
        setHistoryError(tt('Hareket geçmişi yüklenemedi.', 'Could not load movement history.'));
      })
      .finally(() => { if (!cancelled) setHistoryLoading(false); });
    return () => { cancelled = true; };
  }, [open, target, warehouseId]);

  const parsed = Number(String(value).replace(',', '.'));
  const valid = value.trim() !== '' && Number.isFinite(parsed) && (mode === 'delta' || parsed >= 0);

  // Sayım modunda hedef miktar, fark modunda işaretli fark girilir; ikisi de tek bir IN/OUT hareketine çevrilir.
  const delta = useMemo(() => {
    if (!target || !valid) return 0;
    return mode === 'count' ? parsed - target.currentQuantity : parsed;
  }, [mode, parsed, valid, target]);
  const resulting = target ? target.currentQuantity + delta : 0;

  const fmt = (n: number) => n.toLocaleString(localeCode, { maximumFractionDigits: 3 });

  const handleSave = async () => {
    if (!target || !companyId) return;
    if (!reason.trim()) {
      setError(tt('Sebep zorunludur.', 'Reason is required.'));
      return;
    }
    if (!valid || delta === 0) {
      setError(tt('Miktarda değişiklik yok.', 'There is no quantity change.'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await InventoryService.createManualStockMovement({
        companyId,
        productId: target.productId,
        warehouseId,
        warehouseStockId: target.warehouseStockId,
        direction: delta > 0 ? 'IN' : 'OUT',
        quantity: Math.abs(delta),
        unitPrice: target.purchasePrice || 0,
        description: `${mode === 'count' ? tt('Sayım düzeltmesi', 'Count adjustment') : tt('Miktar düzeltme', 'Quantity adjustment')}: ${reason.trim()}`,
        date: new Date().toISOString(),
        createdBy: currentUser?.email || currentUser?.uid || '',
        lotNo: target.trackLots ? lotSelection.lotNo : undefined,
        expiryDate: target.trackLots ? lotSelection.expiryDate : undefined,
        productionDate: target.trackLots ? lotSelection.productionDate : undefined,
        lotId: target.trackLots && lotSelection.mode === 'manual' ? lotSelection.lots[0]?.lotId : undefined,
      });
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || tt('Düzeltme kaydedilemedi.', 'Adjustment could not be saved.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{tt('Miktar Düzelt', 'Adjust Quantity')}</DialogTitle>
      <DialogContent dividers>
        {target && (
          <>
            <Typography variant="subtitle1" fontWeight={600}>{target.productName}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {warehouseName} · {tt('Mevcut', 'Current')}: <strong>{fmt(target.currentQuantity)}</strong> {target.unit || ''}
            </Typography>

            <ToggleButtonGroup
              exclusive
              size="small"
              value={mode}
              onChange={(_, m: Mode | null) => {
                if (!m) return;
                setMode(m);
                setValue(m === 'count' ? String(target.currentQuantity) : '');
              }}
              sx={{ mb: 2 }}
            >
              <ToggleButton value="count">{tt('Sayım (yeni miktar)', 'Count (new quantity)')}</ToggleButton>
              <ToggleButton value="delta">{tt('Ekle/Düş (fark)', 'Add/Subtract (difference)')}</ToggleButton>
            </ToggleButtonGroup>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <TextField
                size="small"
                type="number"
                label={mode === 'count' ? tt('Sayılan miktar', 'Counted quantity') : tt('Fark (+/-)', 'Difference (+/-)')}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                error={value.trim() !== '' && !valid}
                sx={{ width: 220 }}
                autoFocus
              />
              <TextField
                size="small"
                label={tt('Sonuç miktar', 'Resulting quantity')}
                value={valid ? fmt(resulting) : '-'}
                InputProps={{ readOnly: true }}
                sx={{ width: 220 }}
              />
            </Box>

            <TextField
              fullWidth
              required
              size="small"
              multiline
              minRows={2}
              label={tt('Sebep', 'Reason')}
              placeholder={tt('Ör. aylık sayım, fire, kırık ürün', 'E.g. monthly count, waste, broken item')}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />

            {target.trackLots && valid && delta !== 0 && (
              <LotPicker
                itemId={target.productId}
                warehouseId={warehouseId}
                quantity={Math.abs(delta)}
                direction={delta > 0 ? 'IN' : 'OUT'}
                value={lotSelection}
                onChange={setLotSelection}
              />
            )}

            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {tt('Son hareketler', 'Recent movements')}
            </Typography>
            {historyLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>
            ) : historyError ? (
              <Alert severity="warning">{historyError}</Alert>
            ) : history.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {tt('Bu depoda bu ürüne ait hareket yok.', 'No movement for this item in this warehouse.')}
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{tt('Tarih', 'Date')}</TableCell>
                    <TableCell>{tt('Tür', 'Type')}</TableCell>
                    <TableCell align="right">{tt('Miktar', 'Quantity')}</TableCell>
                    <TableCell align="right">{tt('Önce → Sonra', 'Before → After')}</TableCell>
                    <TableCell>{tt('Açıklama', 'Description')}</TableCell>
                    <TableCell>{tt('Kullanıcı', 'User')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((m) => (
                    <TableRow key={m.id}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {m.date ? new Date(m.date).toLocaleString(localeCode) : '-'}
                      </TableCell>
                      <TableCell>{m.movementType || '-'}</TableCell>
                      <TableCell align="right" sx={{ color: m.direction === 'OUT' ? 'error.main' : 'success.main' }}>
                        {m.direction === 'OUT' ? '-' : '+'}{fmt(Math.abs(m.quantity))}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {m.previousQuantity != null && m.newQuantity != null
                          ? `${fmt(Number(m.previousQuantity))} → ${fmt(Number(m.newQuantity))}`
                          : '-'}
                      </TableCell>
                      <TableCell>{m.description || '-'}</TableCell>
                      <TableCell>{m.createdBy || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>{tt('Vazgeç', 'Cancel')}</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !valid || delta === 0 || !reason.trim()}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {tt('Kaydet', 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WarehouseStockAdjustDialog;
